import { useCallback, useMemo, useState } from "react";
import {
  CreateWorkspaceWizardData,
  WizardTabConfig,
} from "../types/create-workspace-wizard";
import {
  validateStep1,
  validateStep2,
  validateStep3,
} from "../utils/create-workspace-validators";
import WizardStep1 from "../components/wizard/wizard-step1/wizard-step1.component";
import WizardStep2 from "../components/wizard/wizard-step2/wizard-step2.component";
import WizardStep3 from "../components/wizard/wizard-step3/wizard-step3.component";
import { showPopupToastMsg, showToastMsg } from "../../../helpers";
import i18n from "i18next";
import { history } from "react-router-guard";
import { WorkspacesService } from "../../../services/workspaces/workspaces.service";
import { LookupsService } from "../../../services/lookups/lookups.service";
import { LocalStorageService } from "../../../services/local-storage/local-storage";
import { UserService } from "../../../services/user/user.service";

const TOTAL_STEPS = 3;

const initialFormData: CreateWorkspaceWizardData = {
  step1: {
    name: "",
    register_number: "",
    address: "",
    postal_code: "",
    city: "",
    email: "",
    technical_contact_email: "",
    organization_contact_email: "",
  },
  step2: {
    workspace: "",
    configurationName: "",
    defaultPassword: "",
    logoPreviewUrl: "",
    logoFile: null,
  },
  step3: {
    coordinatorFirstName: "",
    coordinatorLastName: "",
    coordinatorEmail: "",
    coordinatorCompany: "",
    coordinatorSubscription: "",
  },
};

/** Map wizard data → your API body */
export function buildCreateWorkspacePayload(form: CreateWorkspaceWizardData) {
  return {
    // Step 1 Data
    name: form.step1.name.trim(),
    telephone: form.step1.register_number,
    address: form.step1.address.trim(),
    postal_code: form.step1.postal_code,
    city: form.step1.city.trim(),
    email: form.step1.email,
    technical_contact_email: form.step1.technical_contact_email,
    organization_contact_email: form.step1.organization_contact_email,
    // Step 2 Data
    workspace: form.step2.workspace.trim(),
    configurationName: form.step2.configurationName,
    defaultPassword: form.step2.defaultPassword,
    logoPreviewUrl: form.step2.logoPreviewUrl,
    // Step 3 Data
    coordinatorFirstName: form.step3.coordinatorFirstName.trim(),
    coordinatorLastName: form.step3.coordinatorLastName.trim(),
    coordinatorEmail: form.step3.coordinatorEmail,
    coordinatorCompany: form.step3.coordinatorCompany.trim(),
    coordinatorSubscription: form.step3.coordinatorSubscription,
  };
}

export function useCreateWorkspaceWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const lookupsService = useMemo(() => new LookupsService(), []);
  const workspaceService = useMemo(() => new WorkspacesService(), []);
  const localStorageService = useMemo(() => new LocalStorageService(), []);
  const userService = useMemo(() => new UserService(), []);

  const [formData, setFormData] = useState<CreateWorkspaceWizardData>(
    initialFormData,
  );
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tabConfig: WizardTabConfig[] = useMemo(
    () => [
      {
        id: "step1",
        label: "WORKSPACE_WIZARD.COMPANY_DETAILS",
        validate: validateStep1,
        getData: (f) => f.step1,
        setData: (f, s) => ({ ...f, step1: s }),
      },
      {
        id: "step2",
        label: "WORKSPACE_WIZARD.WORKSPACE_DETAILS",
        validate: validateStep2,
        getData: (f) => f.step2,
        setData: (f, s) => ({ ...f, step2: { ...f.step2, ...s } }),
      },
      {
        id: "step3",
        label: "WORKSPACE_WIZARD.COORDINATOR_ACCOUNT",
        validate: validateStep3,
        getData: (f) => f.step3,
        setData: (f, s) => ({ ...f, step3: s }),
      },
    ],
    [],
  );

  const currentTab = tabConfig[activeStep];
  const currentData = currentTab.getData(formData);

  const isCurrentStepValid = useMemo(() => {
    const e = currentTab.validate(currentData);
    return !e || Object.keys(e).length === 0;
  }, [currentTab, currentData]);

  const stepValidationErrors = useMemo(() => {
    const e = currentTab.validate(currentData);
    return e && Object.keys(e).length > 0 ? e : null;
  }, [currentTab, currentData]);

  const handleStepChange = useCallback(
    (stepData: any) => {
      setFormData((prev) => currentTab.setData(prev, stepData));
    },
    [currentTab],
  );

  const scrollTop = useCallback(() => {
    const el = document.querySelector(".contentPane");
    if (el) el.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const handleNext = useCallback(() => {
    const stepErrors = currentTab.validate(currentData);
    if (stepErrors && Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors(null);
    setActiveStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
    scrollTop();
  }, [currentTab, currentData, scrollTop]);

  const handlePrev = useCallback(() => {
    setErrors(null);
    setActiveStep((s) => Math.max(s - 1, 0));
    scrollTop();
  }, [scrollTop]);

  const handleSubmit = useCallback(async () => {
    const stepErrors = currentTab.validate(currentData);
    if (stepErrors && Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors(null);
    setSubmitting(true);
    try {
      const logoFile = formData.step2.logoFile;
      if (!logoFile) {
        showPopupToastMsg(
          "error",
          i18n.t("THIS_FIELD_IS_REQUIRED", {
            defaultValue: "Logo is required",
          }),
        );
        return;
      }
      const payload = buildCreateWorkspacePayload(formData);
      const workspacePicForm = new FormData();
      workspacePicForm.append("image", logoFile, logoFile.name);
      workspacePicForm.append("clientName", payload.configurationName);
      workspacePicForm.append("type", "logo");
      const workspaceImageRes = await lookupsService.uploadImage(
        workspacePicForm,
      );
      if (!workspaceImageRes?.url) {
        showPopupToastMsg(
          "error",
          i18n.t("SOME_THING_WENT_WRONG", {
            defaultValue: "Something went wrong",
          }),
        );
        return;
      }
      const { logoPreviewUrl: _preview, ...tenantFields } = payload;
      const res = await workspaceService.addNewWorkspace({
        ...tenantFields,
        logoUrl: workspaceImageRes.url,
      });
      if (res?.message === "SUCCESS" && res?.tenantId) {
        await addCoordinatorUser(res.tenantId);
        showToastMsg(
          "success",
          i18n.t("WORKSPACE_WIZARD.WORKSPACE_CREATED_SUCCESSFULLY", {
            defaultValue: "Workspace created successfully",
          }),
        );
        localStorageService.setData("tenantId", res.tenantId);
        localStorageService.setData("tenantIcon", workspaceImageRes.url);
        localStorageService.setData("tenantName", payload.name);
        history.replace("/dashboard");
      } else {
        showPopupToastMsg(
          "error",
          res?.message ??
            i18n.t("SOME_THING_WENT_WRONG", {
              defaultValue: "Something went wrong",
            }),
        );
      }
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, currentData, formData]);

  const addCoordinatorUser = useCallback(
    async (tenantId: number) => {
      try {
        const data: any = {
          firstName: capitalize(formData.step3.coordinatorFirstName),
          lastName: capitalize(formData.step3.coordinatorLastName),
          email: formData.step3.coordinatorEmail,
          role: "COORDINATOR",
          group: "internal",
          plan: formData.step3.coordinatorSubscription,
          company: formData.step3.coordinatorCompany,
          defPassChecked: true,
        };
        await userService.addUser({ users: [data] }, tenantId);
      } catch (error) {
        showPopupToastMsg(
          "error",
          i18n.t("SOME_THING_WENT_WRONG", {
            defaultValue: "Something went wrong",
          }),
        );
      }
    },
    [formData, userService],
  );

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const StepComponent = useMemo(() => {
    switch (activeStep) {
      case 0:
        return WizardStep1;
      case 1:
        return WizardStep2;
      case 2:
        return WizardStep3;
      default:
        return WizardStep1;
    }
  }, [activeStep]);

  return {
    activeStep,
    totalSteps: TOTAL_STEPS,
    tabConfig,
    formData,
    errors,
    submitting,
    currentData,
    isCurrentStepValid,
    stepValidationErrors,
    handleStepChange,
    handleNext,
    handlePrev,
    handleSubmit,
    StepComponent,
  };
}
